import { date } from '@ipfabric/utils';
import './styles/Header.css';

import {
  makeSelectAllSnapshots,
  makeSelectedSnapshot,
} from 'pages/SnapshotManagement/selectors';
import { type FC, useEffect, useState } from 'react';

import { Logo } from '@ipfabric/ui-components/Logo/Logo';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { LicenseLimitChecker } from '@ipfabric/utils/license/licenseLimitChecker';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import type {
  GlobalAuthState,
  GlobalUserState,
  License,
  LoginStatus,
} from 'components/GlobalState/GlobalStateModel';
import type Immutable from 'immutable';
import get from 'lodash/get';
import type { ImmutableSnapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { useSelector } from 'react-redux';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { usePrevious } from 'utils/hooks/usePrevious';
import { useHasPermission } from 'utils/permissions/useHasPermission';
import { versionWithoutBuildNumber } from 'utils/versionWithoutBuildNumber';
import { ClickOutsideDropdownHandler } from './ClickOutsideDropdownHandler';
import { ErrorBox } from './ErrorBox';
import { ExpiredBox } from './ExpiredBox';
import { HeaderAttributeFilter } from './HeaderAttributeFilter/HeaderAttributeFilter';
import JobManagement from './JobManagement';
import Notifications from './Notifications';
import { HeaderSystemStatus } from './Status/HeaderSystemStatus';
import { UserDropdown } from './UserDropdown/UserDropdown';

const LICENSE_SHOW = 90;

interface Props {
  authenticatedAndLicensed: boolean;
  licenseChecker: LicenseLimitChecker;
  licenseInfo?: License;
  loginStatus: LoginStatus;
  msg: any;
  newVersion: string | null;
  user?: GlobalUserState;
  version?: string | null;
  timezone: date.Timezone;
  dateTimeFormat: string;
  auth: GlobalAuthState;
}

interface Selector {
  snapshots: Immutable.List<ImmutableSnapshot>;
  selectedSnapshot: string;
}

export type HeaderDropdownTitle =
  | 'job-management'
  | 'notifications'
  | 'support'
  | 'user-menu';

const HeaderSelector = createStructuredSelector({
  selectedSnapshot: makeSelectedSnapshot(),
  snapshots: makeSelectAllSnapshots(),
});

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
export const Header: FC<Props> = (props) => {
  const [visibleDropdown, setVisibleDropdown] =
    useState<HeaderDropdownTitle | null>(null);
  const { snapshots, selectedSnapshot } = useSelector<unknown, Selector>(
    HeaderSelector,
  );
  const location = useLocation();
  const prevPathname = usePrevious(location.pathname);
  const hasPermission = useHasPermission();

  const {
    authenticatedAndLicensed,
    msg: allMsg,
    msg: {
      app: { header: msg },
    },
    licenseChecker,
    licenseInfo,
    loginStatus,
    newVersion,
    user,
    version,
    timezone,
    dateTimeFormat,
    auth,
  } = props;
  const licenseValidTo = get(licenseInfo, ['validTo'], null);
  const licenseState = get(licenseInfo, ['state']);
  const licenseDeviceLimit = get(licenseInfo, 'devices');
  const licenseCCULimit = get(licenseInfo, 'ccu');
  const additionalFeatures = get(licenseInfo, 'additionalFeatures');
  const remainsDays =
    licenseValidTo !== null
      ? Math.ceil((licenseValidTo - date.getTs()) / 86400000)
      : 0;
  const foundSnapshot = snapshots.find(
    (snapshot) =>
      snapshot !== undefined && snapshot.get('id') === selectedSnapshot,
  );
  const licensedDevCount = foundSnapshot?.get('licensedDevCount');
  const limitExceeded =
    licenseDeviceLimit !== -1 &&
    !licenseChecker.lteDeviceLimit({ devices: licensedDevCount }) &&
    licenseState === 'VALID';
  const remainsTooltip = get(msg, 'licenseBoxExpiresIn', '')
    .replace('{remainsDays}', remainsDays)
    .replace('{days}', remainsDays > 1 ? msg.days : msg.day);

  const toggleVisibleDropdown = (name: HeaderDropdownTitle) => {
    if (visibleDropdown === name) {
      setVisibleDropdown(null);
    } else {
      setVisibleDropdown(name);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies(prevPathname): prevent infinite loop
  useEffect(() => {
    if (prevPathname !== location.pathname) {
      setVisibleDropdown(null);
    }
  }, [location.pathname]);

  return (
    <header className='main-header'>
      <Link className='logo' to='/'>
        <div className='logo-mini'>
          <Logo variant='compact' color='white' height={20} />
        </div>
        <div className='logo-lg w-full'>
          <div className='flex flex-row items-end'>
            <Logo color='white' height={20} />
            <span className='header__logo-version'>
              {versionWithoutBuildNumber(version ?? null)}
            </span>
          </div>
        </div>
      </Link>
      <nav className='navbar navbar-static-top block'>
        {authenticatedAndLicensed && (
          <span className='status-menu status-menu-large float-right'>
            <HeaderSystemStatus />
          </span>
        )}
        {(licenseState === 'EXPIRED' ||
          (licenseState === 'VALID' && remainsDays <= 0)) && (
          <div>
            <Tooltip placement='bottom' content={msg.licenseBoxExpiredTooltip}>
              <ExpiredBox desktopSmall remainingDays={remainsDays}>
                <Link to='/upload-license' className='header__box-link'>
                  <i className='fa fa-clock block' />
                  {msg.licenseBoxExpiredShort}
                </Link>
              </ExpiredBox>
            </Tooltip>
            <ExpiredBox remainingDays={remainsDays}>
              <Link to='/upload-license' className='header__box-link'>
                <i className='fa fa-clock block' />
                {msg.licenseBoxExpired}
              </Link>
            </ExpiredBox>
          </div>
        )}
        {licenseState === 'ERROR' && (
          <div>
            <Tooltip placement='bottom' content={msg.licenseBoxError}>
              <ErrorBox desktopSmall>
                <Link to='/upload-license' className='header__box-link'>
                  <i className='fa fa-clock block' />
                  {msg.licenseBoxErrorShort}
                </Link>
              </ErrorBox>
            </Tooltip>
            <ErrorBox>
              <Link to='/upload-license' className='header__box-link'>
                <i className='fa fa-clock block' />
                {msg.licenseBoxError}
              </Link>
            </ErrorBox>
          </div>
        )}
        {licenseState === 'NOT_FOUND' && (
          <div>
            <Tooltip placement='bottom' content={msg.licenseBoxMissing}>
              <ErrorBox desktopSmall>
                <Link to='/upload-license' className='header__box-link'>
                  <i className='fa fa-clock block' />
                  {msg.licenseBoxMissingShort}
                </Link>
              </ErrorBox>
            </Tooltip>
            <ErrorBox>
              <Link to='/upload-license' className='header__box-link'>
                <i className='fa fa-clock block' />
                {msg.licenseBoxMissing}
              </Link>
            </ErrorBox>
          </div>
        )}
        {licenseState === 'VALID' &&
          remainsDays <= LICENSE_SHOW &&
          remainsDays > 0 && (
            <div>
              <Tooltip content={remainsTooltip}>
                <ExpiredBox remainingDays={remainsDays}>
                  <Link to='/upload-license' className='header__box-link'>
                    <i className='fa fa-clock block' />
                    {msg.licenseBoxExpiresInShort
                      .replace('{remainsDays}', remainsDays)
                      .replace('{days}', remainsDays > 1 ? msg.days : msg.day)}
                  </Link>
                </ExpiredBox>
              </Tooltip>
              <Tooltip placement='bottom' content={remainsTooltip}>
                <ExpiredBox desktopSmall remainingDays={remainsDays}>
                  <Link to='/upload-license' className='header__box-link'>
                    <i className='fa fa-clock block' />
                    {msg.licenseBoxExpiresInShort
                      .replace('{remainsDays}', remainsDays)
                      .replace('{days}', remainsDays > 1 ? msg.days : msg.day)}
                  </Link>
                </ExpiredBox>
              </Tooltip>
            </div>
          )}
        {limitExceeded && (
          <div>
            <Tooltip placement='bottom' content={msg.licenseDeviceLimitTooltip}>
              <div className='header__license-device-limit'>
                <Link to='/license-limit-exceeded' className='header__box-link'>
                  <i className='fa fa-exclamation-triangle block' />{' '}
                  {msg.licenseDeviceLimit}
                </Link>
              </div>
            </Tooltip>
          </div>
        )}
        {newVersion && (
          <Tooltip placement='bottom' content={msg.newUpdate}>
            <span className='header__new-update'>
              <a
                className='header__anchor'
                href={`https://${window.location.hostname}/ipf-system-upgrade/`}
                target='_blank'
                rel='noreferrer'
              >
                <i className='fa fa-cloud-download block' />
                <span>{newVersion}</span>
              </a>
            </span>
          </Tooltip>
        )}
        <div className='navbar-custom-menu ml-0 block w-auto'>
          <ul className='nav navbar-nav header__navbar-nav'>
            {authenticatedAndLicensed && (
              <JobManagement
                toggle={() => toggleVisibleDropdown('job-management')}
                open={visibleDropdown === 'job-management'}
                msg={allMsg}
                dateTimeFormat={dateTimeFormat}
                timezone={timezone}
                auth={auth}
              />
            )}
            <Notifications
              toggle={() => toggleVisibleDropdown('notifications')}
              open={visibleDropdown === 'notifications'}
              msg={allMsg}
              dateTimeFormat={dateTimeFormat}
              timezone={timezone}
            />
            <ClickOutsideDropdownHandler
              className='dropdown'
              open={visibleDropdown === 'support'}
              toggle={() => {
                toggleVisibleDropdown('support');
              }}
            >
              <ButtonAsA
                className='dropdown-toggle header__dropdown-toggle'
                aria-label='Support Dropdown Toggle'
                onClick={() => {
                  toggleVisibleDropdown('support');
                }}
              >
                <i className='fa fa-medkit navbar-nav__icon' />
                {msg.support}
              </ButtonAsA>
              <ul
                aria-label='Support Dropdown Content'
                className={classNames(
                  'dropdown-menu',
                  visibleDropdown === 'support' && 'dropdown-menu--open',
                )}
              >
                {authenticatedAndLicensed && (
                  <>
                    <li>
                      <Link
                        className='header__support-menu-link'
                        to='/support/status'
                      >
                        {msg.statusMessage}
                      </Link>
                    </li>
                    {hasPermission('settings') && (
                      <li>
                        <Link
                          className='header__support-menu-link'
                          to='/support/techsupport'
                        >
                          {msg.supportTechsupport}
                        </Link>
                      </li>
                    )}
                    <li>
                      <Link
                        className='header__support-menu-link'
                        to='/support/diagnostics'
                      >
                        {msg.supportDiagnostics}
                      </Link>
                    </li>
                    <li>
                      <Link
                        className='header__support-menu-link'
                        to='/support/vpn'
                      >
                        {msg.supportVpn}
                      </Link>
                    </li>
                    <li className='divider' />
                  </>
                )}
                <li>
                  <a
                    className='header__support-menu-anchor'
                    href='http://support.ipfabric.io'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {msg.supportPortal}
                  </a>
                </li>
                <li>
                  <a
                    className='header__support-menu-anchor'
                    href='https://docs.ipfabric.io'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {msg.documentation}
                  </a>
                </li>
                <li>
                  <a
                    className='header__support-menu-anchor'
                    href={'/api/rapidoc'}
                    target='_blank'
                    rel='noreferrer'
                  >
                    API Documentation
                  </a>
                </li>
                <li>
                  <a
                    className='header__support-menu-anchor'
                    // Note: this link may not work in various development setups. In the production setup,
                    // upgrade portal is available at this path at the same domain as the main application.
                    href={'/ipf-system-upgrade'}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Manual Upgrade
                  </a>
                </li>
              </ul>
            </ClickOutsideDropdownHandler>
            {authenticatedAndLicensed && (
              <ClickOutsideDropdownHandler
                className='dropdown user user-menu'
                open={visibleDropdown === 'user-menu'}
                toggle={() => {
                  toggleVisibleDropdown('user-menu');
                }}
                dataTestId='user-menu'
              >
                <ButtonAsA
                  className='dropdown-toggle header__dropdown-toggle'
                  onClick={() => {
                    toggleVisibleDropdown('user-menu');
                  }}
                >
                  <div className='user-image header__user-image-top flex items-center justify-center bg-white'>
                    <img
                      alt='User'
                      width='16'
                      height='16'
                      src='/public/img/ipFabric_icon.png'
                    />
                  </div>
                  <br />
                </ButtonAsA>
                {visibleDropdown === 'user-menu' && (
                  <UserDropdown
                    toggleVisibleDropdown={toggleVisibleDropdown}
                    timezone={timezone}
                    dateTimeFormat={dateTimeFormat}
                    user={user}
                    licenseState={licenseState}
                    licenseValidTo={licenseValidTo}
                    additionalFeatures={additionalFeatures}
                    licenseDeviceLimit={licenseDeviceLimit}
                    licenseCCULimit={licenseCCULimit}
                    version={version}
                  />
                )}
              </ClickOutsideDropdownHandler>
            )}
            {loginStatus === 'LOGGED_OUT' && licenseState === 'VALID' && (
              <li>
                <NavLink end to='/login?noredir=1'>
                  <i className='fa fa-user navbar-nav__icon' />
                  {msg.login}
                </NavLink>
              </li>
            )}
          </ul>
        </div>
        {authenticatedAndLicensed && <HeaderAttributeFilter />}
      </nav>
    </header>
  );
};
